<template>
  <div>
    <HeroSection :heroImageUrl="heroImageUrl" :class="$style.heroSection" :showBadge="false">
      <template #title>
        {{ pageTitle }}
      </template>
      <template #body>
        {{ description }}
      </template>
    </HeroSection>
    <FloatingButtons />
    <div :class="['layout-container', $style.contentWrapper]">
      <div class="row">
        <Breadcrumbs class="col-md-8" />
      </div>
      <AnchorLinks />
      <PriceDevelopment />
      <HowItWorksSectionWrapper />
      <LocalContent />
      <ValuationSection />
      <SellingPortalWrapper />
      <QuestionSection />
      <MarketAtlasSection />
      <LocationCarousel :includeCurrentLocation="false" />
      <ArticleSection />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import ArticleSection from '@/components/common/ArticleSection/ArticleSection.vue';
import HeroSection from '@/components/common/HeroSection/HeroSection.vue';
import LocationCarousel from '@/components/common/LocationCarousel/LocationCarousel.vue';
import MarketAtlasSection from '@/components/common/MarketAtlasSection/MarketAtlasSection.vue';
import QuestionSection from '@/components/common/QuestionSection/QuestionSection.vue';
import ValuationSection from '@/components/common/ValuationSection/ValuationSection.vue';
import AnchorLinks from '@/components/local-landing-page/AnchorLinks/AnchorLinks.vue';
import Breadcrumbs from '@/components/local-landing-page/Breadcrumbs/Breadcrumbs.vue';
import FloatingButtons from '@/components/local-landing-page/FloatingButtons/FloatingButtons.vue';
import HowItWorksSectionWrapper from '@/components/local-landing-page/HowItWorksSection/HowItWorksWrapper.vue';
import LocalContent from '@/components/local-landing-page/LocalContent/LocalContent.vue';
import PriceDevelopment from '@/components/local-landing-page/PriceDevelopment/PriceDevelopment.vue';
import SellingPortalWrapper from '@/components/local-landing-page/SellingPortal/SellingPortalWrapper.vue';
import { scrollIntoViewByElement } from '@/helpers/Route/routeHelper';
import { generateMetadataLinks } from '@/pages/LocalLandingPage/metadataTagsHelper';
import { useLocalLandingStore } from '@/stores/localLanding/localLanding';
import { trackingVirtualPageView } from '@/tracking/tracking';
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { useHeroImageUrl } from './composables/useHeroImageUrl';
import { usePageTitle } from './composables/usePageTitle';

// Composables
const localLandingStore = useLocalLandingStore();
const { locale, t } = useI18n();
const route = useRoute();
const { heroImageUrl } = useHeroImageUrl();
const { pageTitle, description } = usePageTitle();
const { routeGeoLocation } = storeToRefs(localLandingStore);

const headData = computed(() =>
  generateMetadataLinks(routeGeoLocation.value, locale.value as HgSupportedLanguage, t),
);

// Head metadata
useHead(headData);

// Watchers
watch(
  routeGeoLocation,
  (newValue) => {
    if (newValue) {
      // Will be blocked automatically on server side
      trackingVirtualPageView({
        subType: 'local_landing_page',
        locale: locale.value,
        stepInputs: '',
        moreParams: { h_search_term_geo_080: newValue.id },
      });
    }
  },
  { deep: true, immediate: true },
);

// Lifecycle
onMounted(() => {
  scrollIntoViewByElement(route.hash);
});
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.contentWrapper[class*='layout-container'] {
  display: flex;
  flex-flow: column nowrap;
  gap: $space-500;
  padding-block: $space-200;

  > div:first-child {
    margin-bottom: -$space-300;
  }
}

.heroSection {
  margin-bottom: 0;
}
</style>
